import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Loader from '@/components/ui/Loader';
import { AUTH_MODES } from '@/configs/authMode';
import { ROUTES } from '@/configs/routes';
import LoginMethodPage from '@/pages/LoginMethodPage';
import UnauthorizedPage from '@/pages/UnauthorizedPage';
import { sessionStore } from '@/stores/global/RootStore';
import { NEXT_URL_PARAM, consumeNextUrl, saveNextUrl } from '@/utils/nextUrl';

/* HOC для страниц, для которых нужна авторизация */
export const withAuth = <P extends Record<string, unknown>>(Component: React.ComponentType<P>) =>
  observer((props: P) => {
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const {
      session,
      isAuthenticated,
      loadSession,
      authMode,
      loadSessionMeta: { isError },
    } = sessionStore;
    const totpEnabled = session?.identity.traits.totpEnabled ?? true;

    React.useEffect(() => {
      if (!isAuthenticated) {
        loadSession();
      }
    }, [isAuthenticated, loadSession]);

    React.useEffect(() => {
      if (pathname === ROUTES.root.path) {
        const next = new URLSearchParams(search).get(NEXT_URL_PARAM);

        if (next) {
          saveNextUrl(next);
        }

        return;
      }

      if (isError) {
        saveNextUrl(pathname + search);
        navigate(`${ROUTES.root.path}?${NEXT_URL_PARAM}=${encodeURIComponent(pathname + search)}`, { replace: true });
      }
    }, [isError, navigate, pathname, search]);

    React.useEffect(() => {
      if (isAuthenticated && !totpEnabled) {
        navigate(ROUTES.auth.totpSetup.path, { replace: true });
      }
    }, [isAuthenticated, navigate, totpEnabled]);

    React.useEffect(() => {
      if (isAuthenticated && totpEnabled) {
        const next = consumeNextUrl();

        if (next) {
          navigate(next, { replace: true });
        }
      }
    }, [isAuthenticated, navigate, totpEnabled]);

    if (!isAuthenticated) {
      if (isError && pathname === ROUTES.root.path) {
        if (authMode === AUTH_MODES.ALFA_ID || authMode === AUTH_MODES.PHONE_TOTP_ONLY) {
          return <LoginMethodPage />;
        }

        return <UnauthorizedPage />;
      }

      return <Loader fullHeight />;
    }

    return <Component {...props} />;
  });
